import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux";
import { useNavigate, useParams } from "react-router-dom";
import StatusMessage from "./status-message";
import {
  Button,
  Iconography,
  IconographySizes,
} from "@hid-galaxy-ui/galaxy-react";
import {
  getExternalContractDetailSagaAction,
  updateExternalContractSagaAction,
} from "../../../../redux/saga/external-contract/saga-actions";
import ExternalContractForm from "../external-contract-form";
import { getValidDateOrNull } from "../../../../utils/getValidDateOrNull";
import "./UpdateExternalContract.scss";

const UpdateExternalContract = () => {
  const [openAccordion, setOpenAccordion] = React.useState<number>(1);
  const [contractDetails, setContractDetails] = React.useState({
    companyName: "",
    salesforceAccount: "",
    accountManagerEmail: "",
    oracleChannel: "",
    country: "",
    region: "",
    soNumber: "",
    salesforceOpportunity: "",
    typeOfEngagement: "",
    revenueStream: "",
    workPackageType: "",
    workPackageAmount: "",
    overallExpiryDate: null,
    supportExpiryDate: null,
    startDate: null,
  });
  const [formSubmitted, setFormSubmitted] = React.useState<boolean>(false);
  const { externalContractDetails, workPackages } = useAppSelector(
    (state) => state.external_contract
  );
  const { ID } = useParams();
  const numberID = Number(ID);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const selectedWorkPackageType = workPackages.find(
    (workPackage) => workPackage.value === contractDetails.workPackageType
  );

  React.useEffect(() => {
    window.scrollTo({ top: 0 });
    dispatch(getExternalContractDetailSagaAction(numberID));
  }, []);

  React.useEffect(() => {
    if (externalContractDetails && externalContractDetails.ID === numberID) {
      setContractDetails({
        ...contractDetails,
        companyName: externalContractDetails.company_name,
        salesforceAccount: externalContractDetails.salesforce_account,
        accountManagerEmail: externalContractDetails.account_manager,
        oracleChannel: externalContractDetails.oracle_channel?.toString() || "",
        country: externalContractDetails.country,
        region: externalContractDetails.region,
        soNumber: externalContractDetails.so_number?.toString() || "",
        salesforceOpportunity: externalContractDetails.salesforce_opportunity,
        typeOfEngagement: externalContractDetails.type_of_engagement,
        revenueStream: externalContractDetails.revenue_stream,
        workPackageType: externalContractDetails.contract_name,
        workPackageAmount: externalContractDetails.contract_amount,
        overallExpiryDate: getValidDateOrNull(externalContractDetails.end_date),
        supportExpiryDate: getValidDateOrNull(
          externalContractDetails.support_expiry_date
        ),
        startDate: getValidDateOrNull(externalContractDetails.start_date),
      });
    }
  }, [externalContractDetails]);

  const formatDateForDB = (date: Date | null) => {
    if (date) {
      const formattedDate = date.toLocaleDateString("en-CA");
      return formattedDate;
    }
    return null;
  };

  const onUpdateClick = () => {
    const contractStatus =
      new Date() <= contractDetails.overallExpiryDate ? "Active" : "Inactive";
    const submitData = {
      ID: externalContractDetails.ID,
      partner_id: externalContractDetails.partner_id,
      company_name: contractDetails.companyName,
      salesforce_account: contractDetails.salesforceAccount,
      account_manager: contractDetails.accountManagerEmail,
      status: contractStatus,
      oracle_channel: Number(contractDetails.oracleChannel),
      country: contractDetails.country,
      region: contractDetails.region,
      so_number: Number(contractDetails.soNumber),
      salesforce_opportunity: contractDetails.salesforceOpportunity,
      type_of_engagement: contractDetails.typeOfEngagement,
      revenue_stream: contractDetails.revenueStream,
      contract_name: contractDetails.workPackageType,
      contract_amount: contractDetails.workPackageAmount,
      end_date: formatDateForDB(contractDetails.overallExpiryDate),
      support_expiry_date: formatDateForDB(contractDetails.supportExpiryDate),
      start_date: formatDateForDB(contractDetails.startDate),
      source: externalContractDetails.source,
      collection: selectedWorkPackageType?.metadata?.collection,
      sku: selectedWorkPackageType?.metadata?.part_number,
    };
    dispatch(updateExternalContractSagaAction(submitData));
    setFormSubmitted(true);
  };

  const isNextButtonEnabled =
    contractDetails.companyName &&
    emailRegex.test(contractDetails.accountManagerEmail) &&
    contractDetails.region &&
    contractDetails.country;

  const isSaveButtonEnabled =
    contractDetails.revenueStream &&
    contractDetails.workPackageType &&
    contractDetails.workPackageAmount &&
    contractDetails.startDate &&
    contractDetails.overallExpiryDate &&
    (contractDetails.supportExpiryDate ||
      !selectedWorkPackageType?.metadata.support_expiry);

  return (
    <div className="update-external-contract-container">
      {formSubmitted ? (
        <StatusMessage ID={externalContractDetails?.partner_id} />
      ) : (
        <>
          <div className="form-title">Update External Contract</div>
          <div className="accordion-no-1">
            <div className="accordion-title">
              <Iconography icon="ballot" size={IconographySizes.Medium} />
              <p>General & Sales Information</p>
            </div>
            {openAccordion === 1 && (
              <>
                <div className="accordion-body">
                  <ExternalContractForm
                    contractDetails={contractDetails}
                    setContractDetails={setContractDetails}
                    formPage="General & Sales Information"
                  />
                  <div className="button-group">
                    <Button
                      variant="secondary"
                      label="Cancel"
                      onClick={() => {
                        navigate(
                          `/hid-admin/dashboard/partner-organizations/${externalContractDetails?.partner_id}/services-and-subscriptions`,
                          { state: { selectedTab: "manage-contracts" } }
                        );
                      }}
                    />
                    <Button
                      variant="primary"
                      label="Next"
                      disabled={!isNextButtonEnabled}
                      onClick={() => {
                        setOpenAccordion(2);
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="accordion-no-2">
            <div
              className={`accordion-title ${
                openAccordion < 2 ? "disabled" : ""
              }`}
            >
              <Iconography icon="addressBook" size={IconographySizes.Medium} />
              <p>Engagement & Expiry</p>
            </div>
            {openAccordion === 2 && (
              <div className="accordion-body">
                <ExternalContractForm
                  contractDetails={contractDetails}
                  setContractDetails={setContractDetails}
                  formPage="Engagement & Expiry"
                />
                <div className="button-group">
                  <Button
                    variant="secondary"
                    label="Back"
                    onClick={() => {
                      setOpenAccordion(1);
                    }}
                  />
                  <Button
                    variant="primary"
                    label="Update"
                    disabled={!isSaveButtonEnabled}
                    onClick={() => {
                      onUpdateClick();
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UpdateExternalContract;
